// GlobalFilterPanel - UI Component for global filter management
// Extracted from stores/GlobalFilterContext for proper separation of concerns

import React, { useState, useEffect } from 'react';
import { useGlobalFilters } from '../../../stores/GlobalFilterContext/GlobalFilterContext';
import { useOcelContext, useOcelMetadata } from '../../../stores/OcelContext';
import { apiClient } from '../../../services/api/client';

const GlobalFilterPanel = ({ activeFile, disableFixedPositioning = false }) => {
  const {
    draftState,
    dispatch,
    filterSummary,
    filterPanelOpen,
    toggleFilterPanel,
    edgeSelectMode,
    edgeDurationMode,
    availableActivities,
  } = useGlobalFilters();

  const { isMetadataLoading } = useOcelContext() || {};
  // Dataset-guarded: null until the metadata for THIS browser's active file arrives
  const ocelMetadata = useOcelMetadata(activeFile);

  const [newEventAttributeName, setNewEventAttributeName] = useState('');
  const [newEventAttributeValue, setNewEventAttributeValue] = useState('');
  const [newObjectAttributeName, setNewObjectAttributeName] = useState('');
  const [newObjectAttributeValue, setNewObjectAttributeValue] = useState('');
  const [availableObjectAttributes, setAvailableObjectAttributes] = useState([]);
  const [availableEventAttributes, setAvailableEventAttributes] = useState([]);
  const [availableEventAttributeValues, setAvailableEventAttributeValues] = useState([]);
  const [availableObjectAttributeValues, setAvailableObjectAttributeValues] = useState([]);
  const [isLoadingValues, setIsLoadingValues] = useState(false);
  const [isLoadingObjectValues, setIsLoadingObjectValues] = useState(false);
  const [datasetDateRange, setDatasetDateRange] = useState(null);
  const [availableObjectTypes, setAvailableObjectTypes] = useState([]);

  // Sync local state from OcelContext metadata whenever it changes or panel opens.
  // No API call — data is already loaded by OCELFileManager.
  useEffect(() => {
    if (!filterPanelOpen || !ocelMetadata) return;

    setAvailableObjectTypes(
      ocelMetadata.object_types
        ? Object.keys(ocelMetadata.object_types)
        : ['Angebot', 'Auftrag', 'Lieferschein']
    );

    if (ocelMetadata.object_attribute_names) {
      setAvailableObjectAttributes(ocelMetadata.object_attribute_names);
    }

    if (ocelMetadata.event_attribute_names) {
      setAvailableEventAttributes(ocelMetadata.event_attribute_names);
    } else {
      setAvailableEventAttributes(['status', 'priority', 'department', 'user', 'category']);
    }

    setDatasetDateRange(
      ocelMetadata.date_range
        ? {
            startDate: ocelMetadata.date_range.start ? ocelMetadata.date_range.start.split('T')[0] : null,
            endDate: ocelMetadata.date_range.end ? ocelMetadata.date_range.end.split('T')[0] : null,
          }
        : null
    );
  }, [filterPanelOpen, ocelMetadata]);

  // Fetch attribute values when attribute is selected
  const fetchEventAttributeValues = async (attributeName) => {
    if (!attributeName) {
      setAvailableEventAttributeValues([]);
      setIsLoadingValues(false);
      return;
    }
    setIsLoadingValues(true);
    try {
      const response = await apiClient.get(
        `/ocel/event-attributes/${encodeURIComponent(attributeName)}/values`,
        { params: { file_type: activeFile || undefined } }
      );
      const values = response.data;
      setAvailableEventAttributeValues(Array.isArray(values) ? values : []);
    } catch (error) {
      console.error('Error fetching event attribute values:', error);
      setAvailableEventAttributeValues([]);
    } finally {
      setIsLoadingValues(false);
    }
  };

  const fetchObjectAttributeValues = async (attributeName) => {
    if (!attributeName) {
      setAvailableObjectAttributeValues([]);
      setIsLoadingObjectValues(false);
      return;
    }
    setIsLoadingObjectValues(true);
    try {
      const response = await apiClient.get(
        `/ocel/object-attributes/${encodeURIComponent(attributeName)}/values`,
        { params: { file_type: activeFile || undefined } }
      );
      const values = response.data;
      setAvailableObjectAttributeValues(Array.isArray(values) ? values : []);
    } catch (error) {
      console.error('Error fetching object attribute values:', error);
      setAvailableObjectAttributeValues([]);
    } finally {
      setIsLoadingObjectValues(false);
    }
  };

  useEffect(() => {
    if (newEventAttributeName) {
      fetchEventAttributeValues(newEventAttributeName);
    } else {
      setAvailableEventAttributeValues([]);
    }
  }, [newEventAttributeName]);

  useEffect(() => {
    if (newObjectAttributeName) {
      fetchObjectAttributeValues(newObjectAttributeName);
    } else {
      setAvailableObjectAttributeValues([]);
    }
  }, [newObjectAttributeName]);

  const toggleObjectType = (objectType) => dispatch({ type: 'TOGGLE_DRAFT_OBJECT_TYPE', payload: { objectType } });
  const setDateRange = (range) => dispatch({ type: 'SET_DRAFT_DATE_RANGE', payload: range });

  const handleAddEventAttributeFilter = () => {
    if (newEventAttributeName && newEventAttributeValue) {
      dispatch({
        type: 'ADD_DRAFT_EVENT_ATTRIBUTE_FILTER',
        payload: {
          attribute: newEventAttributeName,
          value: newEventAttributeValue,
          enabled: true,
          mode: 'include'
        }
      });
      setNewEventAttributeName('');
      setNewEventAttributeValue('');
      setAvailableEventAttributeValues([]);
    }
  };

  const handleAddObjectAttributeFilter = () => {
    if (newObjectAttributeName && newObjectAttributeValue) {
      dispatch({
        type: 'ADD_DRAFT_OBJECT_ATTRIBUTE_FILTER',
        payload: {
          attribute: newObjectAttributeName,
          value: newObjectAttributeValue,
          enabled: true
        }
      });
      setNewObjectAttributeName('');
      setNewObjectAttributeValue('');
      setAvailableObjectAttributeValues([]);
    }
  };

  const handleApply = () => {
    dispatch({ type: 'APPLY_FILTERS' });
  };

  const handleClear = () => {
    dispatch({ type: 'CLEAR_FILTERS' });
  };

  if (!filterPanelOpen) {
    return (
      <div className={disableFixedPositioning ? "" : "fixed top-4 right-[340px] z-50"}>
        <button
          onClick={toggleFilterPanel}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-md hover:shadow-lg transition-shadow"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707v4.172a1 1 0 01-.632.928l-2 .8a1 1 0 01-1.368-.928v-5a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          <span>Filters</span>
          {filterSummary.hasFilters && (
            <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
              {filterSummary.count}
            </span>
          )}
        </button>
      </div>
    );
  }

  // Edge Duration Filter helpers
  const edgeDuration = draftState.edgeDurationFilters?.[0] || null;
  const unitToMs = (unit) => {
    switch (unit) {
      case 'seconds': return 1000;
      case 'minutes': return 60 * 1000;
      case 'hours': return 60 * 60 * 1000;
      case 'days': return 24 * 60 * 60 * 1000;
      case 'weeks': return 7 * 24 * 60 * 60 * 1000;
      case 'months': return 30 * 24 * 60 * 60 * 1000;
      default: return 1000;
    }
  };
  const handleEdgeDurationChange = ({ operator, value, unit, mode }) => {
    const updates = {};
    if (operator !== undefined) updates.operator = operator;
    if (mode !== undefined) updates.mode = mode;
    if (value !== undefined) updates.value = value;
    if (unit !== undefined) updates.unit = unit;

    const currentFilter = edgeDuration || {};
    const finalValue = value !== undefined ? value : currentFilter.value;
    const finalUnit = unit !== undefined ? unit : currentFilter.unit;

    if (finalValue != null && finalValue !== '' && finalUnit) {
      updates.thresholdMs = Math.max(0, Number(finalValue)) * unitToMs(finalUnit);
    } else {
      updates.thresholdMs = null;
    }

    dispatch({ type: 'UPDATE_DRAFT_EDGE_DURATION_FILTER', payload: { updates } });
  };

  return (
    <div className="fixed top-0 right-0 h-full w-96 bg-white shadow-2xl z-50 border-l border-gray-200 overflow-y-auto overscroll-contain">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Global Filters</h2>
          <button onClick={toggleFilterPanel} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Loading state during file switch */}
        {isMetadataLoading && (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <svg className="w-8 h-8 animate-spin text-blue-600" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            <span className="text-sm text-gray-500">Loading dataset...</span>
          </div>
        )}

        {!isMetadataLoading && (<>
        {/* Object Types */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Object Types</label>
          <div className="space-y-2">
            {availableObjectTypes.length > 0 ? (
              availableObjectTypes.map(type => (
                <label key={type} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={draftState.objectTypes.includes(type)}
                    onChange={() => toggleObjectType(type)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">{type}</span>
                </label>
              ))
            ) : (
              <div className="text-sm text-gray-500">Loading object types...</div>
            )}
          </div>
        </div>

        {/* Edge Duration Filter */}
        {edgeDuration && (
          <div className="mb-6 border border-blue-200 rounded-md p-4 bg-blue-50">
            <div className="mb-3 text-sm text-gray-600">Duration filter for Edge
              <span className="ml-1 font-medium text-gray-800">{edgeDuration.edgeLabel || edgeDuration.edgeId}</span>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Operator</label>
                <select
                  value={edgeDuration.operator || 'le'}
                  onChange={(e) => handleEdgeDurationChange({ operator: e.target.value })}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                >
                  <option value="le">≤</option>
                  <option value="ge">≥</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Mode</label>
                <select
                  value={edgeDuration.mode || 'include'}
                  onChange={(e) => handleEdgeDurationChange({ mode: e.target.value })}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                >
                  <option value="include">Include</option>
                  <option value="exclude">Exclude</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Threshold value</label>
                <input
                  type="number"
                  min="0"
                  value={edgeDuration.value || ''}
                  onChange={(e) => handleEdgeDurationChange({ value: e.target.value })}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                  placeholder="e.g., 2"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Unit</label>
                <select
                  value={edgeDuration.unit || 'days'}
                  onChange={(e) => handleEdgeDurationChange({ unit: e.target.value })}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                >
                  <option value="seconds">seconds</option>
                  <option value="minutes">minutes</option>
                  <option value="hours">hours</option>
                  <option value="days">days</option>
                  <option value="weeks">weeks</option>
                  <option value="months">months</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  dispatch({ type: 'APPLY_EDGE_DURATION_FILTER' });
                  dispatch({ type: 'APPLY_FILTERS' });
                }}
                disabled={!edgeDuration.value || edgeDuration.value === ''}
                className={`px-4 py-2 text-sm font-medium text-white rounded transition-colors ${
                  !edgeDuration.value || edgeDuration.value === ''
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                Apply Edge Duration Filter
              </button>
              <button
                onClick={() => dispatch({ type: 'CLEAR_EDGE_DURATION_FILTER' })}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded transition-colors"
              >
                Clear
              </button>
            </div>
          </div>
        )}

        {/* Edge Filters Section */}
        <div className="mb-6 border border-indigo-200 rounded-md p-4 bg-indigo-50">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Edge Filters</h3>
          <div className="mb-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={edgeDurationMode}
                onChange={() => dispatch({ type: 'TOGGLE_EDGE_DURATION_MODE' })}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">Edge Duration Mode</span>
            </label>
            {edgeDurationMode && (
              <p className="mt-1 text-xs text-gray-600">Click an edge in the graph to configure duration filter</p>
            )}
          </div>
          <div className="mb-0">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={edgeSelectMode}
                onChange={() => dispatch({ type: 'TOGGLE_EDGE_SELECT_MODE' })}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">Edge Select Mode (Containment)</span>
            </label>
            {edgeSelectMode && (
              <p className="mt-1 text-xs text-gray-600">Click edges in the graph to add them to the containment filter</p>
            )}
          </div>
        </div>

        {/* Edge Containment Filter Configuration */}
        {draftState.edgeContainmentFilters.edges.length > 0 && (
          <div className="mb-6 border border-blue-200 rounded-md p-4 bg-blue-50">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Edge Containment Filter</h3>
            <div className="space-y-2 mb-3">
              {draftState.edgeContainmentFilters.edges.map((edge, index) => (
                <div key={index} className="flex items-center gap-2 p-2 bg-white rounded border border-gray-200">
                  <span className="text-sm flex-grow">{edge.sourceActivity} → {edge.targetActivity}</span>
                  <button
                    onClick={() => dispatch({ type: 'REMOVE_EDGE_FROM_CONTAINMENT_FILTER', payload: index })}
                    className="text-red-500 hover:text-red-700 p-1 font-bold text-lg leading-none"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <div className="mb-3">
              <label className="block text-xs text-gray-600 mb-1">Objects must contain:</label>
              <select
                value={draftState.edgeContainmentFilters.matchType}
                onChange={(e) => dispatch({ type: 'UPDATE_EDGE_CONTAINMENT_CONFIG', payload: { matchType: e.target.value } })}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
              >
                <option value="any">Any selected edge</option>
                <option value="all">All selected edges</option>
              </select>
            </div>
            <div className="mb-3">
              <label className="block text-xs text-gray-600 mb-1">Filter mode:</label>
              <select
                value={draftState.edgeContainmentFilters.mode}
                onChange={(e) => dispatch({ type: 'UPDATE_EDGE_CONTAINMENT_CONFIG', payload: { mode: e.target.value } })}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
              >
                <option value="include">Include matching objects</option>
                <option value="exclude">Exclude matching objects</option>
              </select>
            </div>
            <button
              onClick={() => {
                dispatch({ type: 'APPLY_EDGE_CONTAINMENT_FILTER' });
                dispatch({ type: 'APPLY_FILTERS' });
              }}
              disabled={draftState.edgeContainmentFilters.edges.length === 0}
              className={`w-full px-3 py-2 text-sm font-medium text-white rounded transition-colors ${
                draftState.edgeContainmentFilters.edges.length === 0
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              Apply Edge Containment Filter
            </button>
          </div>
        )}

        {/* Activity Node Filter */}
        <div className="mb-6 border border-blue-200 rounded-md p-4 bg-blue-50">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Activity Node Filter</h3>
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-600 mb-1">Select Activities</label>
            <select
              multiple
              size="5"
              className="w-full border border-gray-300 rounded p-2 text-sm"
              value={draftState.activityNodeFilters.nodes}
              onChange={(e) => {
                const selected = Array.from(e.target.selectedOptions, option => option.value);
                dispatch({ type: 'UPDATE_ACTIVITY_NODE_CONFIG', payload: { nodes: selected } });
              }}
            >
              {availableActivities.length > 0 ? (
                availableActivities.map((activity) => {
                  const activityName = typeof activity === 'string' ? activity : activity.name;
                  return (
                    <option key={activityName} value={activityName}>{activityName}</option>
                  );
                })
              ) : (
                <option disabled>Loading activities...</option>
              )}
            </select>
            <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple</p>
          </div>
          {draftState.activityNodeFilters.nodes.length > 0 && (
            <div className="mb-3 space-y-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">Selected Activities:</label>
              {draftState.activityNodeFilters.nodes.map((node, index) => (
                <div key={index} className="flex items-center gap-2 p-2 bg-white rounded border border-gray-200">
                  <span className="text-sm flex-grow">{node}</span>
                  <button
                    onClick={() => dispatch({ type: 'REMOVE_ACTIVITY_NODE', payload: { index } })}
                    className="text-red-600 hover:text-red-800 font-bold text-lg"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-600 mb-1">Match Type</label>
            <select
              value={draftState.activityNodeFilters.matchType}
              onChange={(e) => dispatch({ type: 'UPDATE_ACTIVITY_NODE_CONFIG', payload: { matchType: e.target.value } })}
              className="w-full border border-gray-300 rounded p-2 text-sm"
            >
              <option value="any">Match any selected activity</option>
              <option value="all">Match all selected activities</option>
            </select>
          </div>
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-600 mb-1">Filter Mode</label>
            <select
              value={draftState.activityNodeFilters.mode}
              onChange={(e) => dispatch({ type: 'UPDATE_ACTIVITY_NODE_CONFIG', payload: { mode: e.target.value } })}
              className="w-full border border-gray-300 rounded p-2 text-sm"
            >
              <option value="include">Include matching objects</option>
              <option value="exclude">Exclude matching objects</option>
            </select>
          </div>
          <button
            disabled={draftState.activityNodeFilters.nodes.length === 0}
            onClick={() => {
              dispatch({ type: 'APPLY_ACTIVITY_NODE_FILTER' });
              dispatch({ type: 'APPLY_FILTERS' });
            }}
            className="w-full px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded transition-colors"
          >
            Apply Activity Node Filter
          </button>
        </div>

        {/* Date Range */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Date Range
            {datasetDateRange && (
              <span className="text-xs text-gray-500 ml-2">
                ({datasetDateRange.startDate} to {datasetDateRange.endDate})
              </span>
            )}
          </label>
          <div className="space-y-3">
            <input
              type="date"
              value={draftState.dateRange.startDate || ''}
              onChange={(e) => setDateRange({ startDate: e.target.value, enabled: true })}
              min={datasetDateRange?.startDate}
              max={datasetDateRange?.endDate}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="date"
              value={draftState.dateRange.endDate || ''}
              onChange={(e) => setDateRange({ endDate: e.target.value, enabled: true })}
              min={datasetDateRange?.startDate}
              max={datasetDateRange?.endDate}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Event Attribute Filters */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Event Attribute Filters</label>
          {draftState.eventAttributeFilters.length > 0 && (
            <div className="space-y-2 mb-2">
              <div className="flex items-center gap-4 p-2 bg-gray-50 rounded-md">
                <span className="text-xs font-medium text-gray-700">Match:</span>
                {['any', 'all'].map(m => (
                  <label key={m} className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="global-eaf-match"
                      value={m}
                      checked={(draftState.eventAttributeFilterSettings?.matchType || 'all') === m}
                      onChange={() => dispatch({ type: 'UPDATE_DRAFT_EVENT_ATTRIBUTE_FILTER_SETTINGS', payload: { matchType: m } })}
                      className="mr-1.5 h-3.5 w-3.5 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-xs text-gray-700">{m === 'any' ? 'Any' : 'All'}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="space-y-2">
            {draftState.eventAttributeFilters.map((filter, index) => (
              <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <input
                  type="checkbox"
                  checked={filter.enabled}
                  onChange={(e) => dispatch({ type: 'UPDATE_DRAFT_EVENT_ATTRIBUTE_FILTER', payload: { index, updates: { enabled: e.target.checked } } })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm flex-grow">{filter.attribute}</span>
                <select
                  value={filter.mode || 'include'}
                  onChange={(e) => dispatch({ type: 'UPDATE_DRAFT_EVENT_ATTRIBUTE_FILTER', payload: { index, updates: { mode: e.target.value } } })}
                  className="px-1 py-0.5 text-sm border border-gray-300 rounded bg-white"
                  title={(filter.mode || 'include') === 'include' ? 'Include matching events' : 'Exclude matching events'}
                >
                  <option value="include">=</option>
                  <option value="exclude">≠</option>
                </select>
                <span className="text-sm flex-grow">{filter.value}</span>
                <button
                  onClick={() => dispatch({ type: 'REMOVE_DRAFT_EVENT_ATTRIBUTE_FILTER', payload: index })}
                  className="text-red-500 hover:text-red-700 p-1"
                >
                  ×
                </button>
              </div>
            ))}
            <div className="mt-3 p-3 border border-gray-200 rounded-md">
              <div className="space-y-2">
                <select
                  value={newEventAttributeName}
                  onChange={(e) => setNewEventAttributeName(e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                >
                  <option value="">Select event attribute</option>
                  {availableEventAttributes.map(attr => (
                    <option key={attr} value={attr}>{attr}</option>
                  ))}
                </select>
                <select
                  value={newEventAttributeValue}
                  onChange={(e) => setNewEventAttributeValue(e.target.value)}
                  disabled={!newEventAttributeName}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded disabled:bg-gray-100"
                >
                  <option value="">Select value</option>
                  {availableEventAttributeValues.length > 0 ? (
                    availableEventAttributeValues.map(value => (
                      <option key={value} value={value}>{value}</option>
                    ))
                  ) : newEventAttributeName && (
                    isLoadingValues
                      ? <option disabled>Loading values...</option>
                      : <option disabled>No values available</option>
                  )}
                </select>
                <button
                  onClick={handleAddEventAttributeFilter}
                  disabled={!newEventAttributeName || !newEventAttributeValue}
                  className="w-full px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Add Event Filter
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Object Attribute Value Filters */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Object Attribute Value Filters</label>
          {draftState.objectAttributeFilters.length > 0 && (
            <div className="space-y-2 mb-2">
              <div className="flex items-center gap-4 p-2 bg-gray-50 rounded-md">
                <span className="text-xs font-medium text-gray-700">Mode:</span>
                {['include', 'exclude'].map(m => (
                  <label key={m} className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="global-oaf-mode"
                      value={m}
                      checked={(draftState.objectAttributeFilterSettings?.mode || 'include') === m}
                      onChange={() => dispatch({ type: 'UPDATE_DRAFT_OBJECT_ATTRIBUTE_FILTER_SETTINGS', payload: { mode: m } })}
                      className="mr-1.5 h-3.5 w-3.5 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-xs text-gray-700">{m === 'include' ? 'Include' : 'Exclude'}</span>
                  </label>
                ))}
              </div>
              <div className="flex items-center gap-4 p-2 bg-gray-50 rounded-md">
                <span className="text-xs font-medium text-gray-700">Match:</span>
                {['any', 'all'].map(m => (
                  <label key={m} className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="global-oaf-match"
                      value={m}
                      checked={(draftState.objectAttributeFilterSettings?.matchType || 'all') === m}
                      onChange={() => dispatch({ type: 'UPDATE_DRAFT_OBJECT_ATTRIBUTE_FILTER_SETTINGS', payload: { matchType: m } })}
                      className="mr-1.5 h-3.5 w-3.5 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-xs text-gray-700">{m === 'any' ? 'Any' : 'All'}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="space-y-2">
            {draftState.objectAttributeFilters.map((filter, index) => (
              <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <input
                  type="checkbox"
                  checked={filter.enabled}
                  onChange={(e) => dispatch({ type: 'UPDATE_DRAFT_OBJECT_ATTRIBUTE_FILTER', payload: { index, updates: { enabled: e.target.checked } } })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm flex-grow">{filter.attribute} = {filter.value}</span>
                <button
                  onClick={() => dispatch({ type: 'REMOVE_DRAFT_OBJECT_ATTRIBUTE_FILTER', payload: index })}
                  className="text-red-500 hover:text-red-700 p-1"
                >
                  ×
                </button>
              </div>
            ))}
            <div className="mt-3 p-3 border border-gray-200 rounded-md">
              <div className="space-y-2">
                <select
                  value={newObjectAttributeName}
                  onChange={(e) => setNewObjectAttributeName(e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                >
                  <option value="">Select object attribute</option>
                  {availableObjectAttributes.map(attr => (
                    <option key={attr} value={attr}>{attr}</option>
                  ))}
                </select>
                <select
                  value={newObjectAttributeValue}
                  onChange={(e) => setNewObjectAttributeValue(e.target.value)}
                  disabled={!newObjectAttributeName}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded disabled:bg-gray-100"
                >
                  <option value="">Select value</option>
                  {availableObjectAttributeValues.length > 0 ? (
                    availableObjectAttributeValues.map(value => (
                      <option key={value} value={value}>{value}</option>
                    ))
                  ) : (
                    newObjectAttributeName && (
                      <option disabled>
                        {isLoadingObjectValues ? 'Loading values...' : 'No values available'}
                      </option>
                    )
                  )}
                </select>
                <button
                  onClick={handleAddObjectAttributeFilter}
                  disabled={!newObjectAttributeName || !newObjectAttributeValue}
                  className="w-full px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Add Object Filter
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={handleClear}
            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          >
            Clear All
          </button>
          <button
            onClick={handleApply}
            className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
          >
            Apply
          </button>
        </div>
        </>)}
      </div>
    </div>
  );
};

export default GlobalFilterPanel;
